import './TransferTeamOwnershipDialog.scss';

import * as React from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import { TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { useSelectedTeamQuery } from '~teacher/components/Account/hooks';
import { selectedPublicIdsAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/atoms';
import { useTeamsMembers } from '~teacher/hook/team';
import { useTransferTeamOwnership } from '~teacher/hook/team/useTransferTeamOwnership';

export const isDialogOpenAtom = atom<boolean>(false);

export function TransferTeamOwnershipDialog() {
  const [isOpen, setIsOpen] = useAtom(isDialogOpenAtom);
  const [isLoading, setIsLoading] = React.useState(false);
  const [agreeToLeaveVisibleBillingInfo, setAgreeToLeaveVisibleBillingInfo] =
    React.useState(false);
  const [agreeToRetainPaymentMethods, setAgreeToRetainPaymentMethods] =
    React.useState(false);

  const selectedPublicId = useAtomValue(selectedPublicIdsAtom.valueAtom);
  const resetSelectedPublicId = useSetAtom(selectedPublicIdsAtom.resetAtom);
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const useSelectedTeam = useSelectedTeamQuery();
  const useSelectedTeamMembersQuery = useTeamsMembers({
    teamId: selectedTeamId,
  });
  const transferOwnershipMutation = useTransferTeamOwnership();
  const newOwnerPublicId = selectedPublicId.values().next().value;
  const previousOwnerData = useSelectedTeamMembersQuery?.data?.find(
    (member) => member.role === TeamRole.Owner
  );

  const onClose = () => {
    resetSelectedPublicId();
    setIsOpen(false);
  };

  const handleTransferOwnership = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    transferOwnershipMutation.mutate(
      {
        teamId: selectedTeamId,
        newOwnerPublicId: newOwnerPublicId,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          onClose();

          // Analytics
          sendTeacherAmplitudeEvent(EventTypes.TeamsTransferOwnership, {
            team_id: selectedTeamId,
            new_owner_public_id: newOwnerPublicId,
            previous_owner_public_id: previousOwnerData.publicId,
          });
        },
        onError: (error) => {
          if (__DEV__) {
            console.error(error);
          }

          toast.critical(translate('Unknown Error. Please try again later.'));
          setIsLoading(false);
        },
      }
    );
  };

  if (!useSelectedTeam.data || !useSelectedTeamMembersQuery.data) {
    return null;
  }

  const TeamName = useSelectedTeam?.data?.displayName?.trim();
  const newOwnerName = useSelectedTeamMembersQuery?.data?.find(
    (member) => member.publicId === newOwnerPublicId
  )?.name;

  return (
    <Dialog
      id="transfer-ownership-dialog"
      open={isOpen}
      onClose={onClose}
      title={translate('Transfer Ownership')}
    >
      <form className="grid gap-5 pt-5" onSubmit={handleTransferOwnership}>
        <Divider />
        <p className="text-base leading-8">
          {translate(
            'Are you sure you want to make "{0}" the Owner of "{1}"? You can’t undo this action.'
          ).format(newOwnerName, TeamName ?? '')}
        </p>
        <Divider />
        <div className="flex gap-2">
          <SlCheckbox
            role="checkbox"
            className="py-2"
            checked={agreeToLeaveVisibleBillingInfo}
            onSlChange={(event: any) => {
              setAgreeToLeaveVisibleBillingInfo(event.target.checked);
            }}
          >
            <span className="text-base leading-8">
              {translate(
                'I understand that the new Owner will be able to edit billing information and view all past invoices and transaction details.'
              )}
            </span>
          </SlCheckbox>
        </div>
        <Divider />
        <div className="flex flex-col gap-2 mb-2">
          <SlCheckbox
            role="checkbox"
            className="py-2"
            checked={agreeToRetainPaymentMethods}
            onSlChange={(event: any) => {
              setAgreeToRetainPaymentMethods(event.target.checked);
            }}
          >
            <span className="text-base leading-8">
              {translate(
                'I understand that I am responsible for deleting any saved payment methods I do not want charged before transferring ownership.'
              )}
            </span>
          </SlCheckbox>
        </div>
        <Divider />
        <div className="flex justify-end gap-4">
          <SmallButton
            appearance="default"
            onClick={onClose}
            isDisabled={isLoading}
          >
            {translate('Cancel')}
          </SmallButton>
          <SmallButton
            appearance="ghost-destructive"
            isLoading={isLoading}
            type="submit"
            isDisabled={
              isLoading ||
              !agreeToLeaveVisibleBillingInfo ||
              !agreeToRetainPaymentMethods
            }
          >
            {translate('Transfer Ownership')}
          </SmallButton>
        </div>
      </form>
    </Dialog>
  );
}

TransferTeamOwnershipDialog.useOpenDialog = () => {
  const setIsOpen = useSetAtom(isDialogOpenAtom);

  return () => {
    setIsOpen(true);
  };
};
